import { Component, Input, ViewChild, inject } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DividerComponent } from '@tecalisdev/infinity';

import { LanguageService } from '../../../../core/services/language.service';
import { ReactiveFormsService } from '../../../../shared/services/reactive-forms.service';
import { IFormFieldPreset } from '../form-field-presets';
import { firstInvalidFormStepOptionRef, markFormStepOptionsTouched } from '../form-step-options.helper';
import { focusFormStepOptionFieldIfInvalid } from '../form-step-option-locale-ui.util';
import {
	firstInvalidFormStepFieldTitleRef,
	focusFormStepFieldTitle,
	markFormStepFieldTitlesTouched,
	presetShowsFieldTitleEditor,
} from '../form-step-field-title.util';
import { FormStepFieldsSectionComponent } from './form-step-fields-section.component';
import { FormStepInstructionsComponent } from './form-step-instructions.component';

@Component({
	selector: 'app-form-step-editable-preset-config',
	imports: [ReactiveFormsModule, DividerComponent, FormStepInstructionsComponent, FormStepFieldsSectionComponent],
	templateUrl: './form-step-editable-preset-config.component.html',
	styleUrl: './form-step-editable-preset-config.component.scss',
})
export class FormStepEditablePresetConfigComponent {
	@Input({ required: true }) form!: FormGroup;
	@Input({ required: true }) fieldsArray!: FormArray;
	@Input() preset?: IFormFieldPreset;
	@Input() primaryField: FormGroup | null = null;

	@ViewChild(FormStepInstructionsComponent)
	instructions?: FormStepInstructionsComponent;

	private languageService = inject(LanguageService);
	private reactiveFormService = inject(ReactiveFormsService);

	public focusLocaleValidationErrors(): void {
		this.instructions?.markLocaleFieldsTouched();
		markFormStepOptionsTouched(this.fieldsArray);
		markFormStepFieldTitlesTouched(this.fieldsArray);

		if (this.instructions?.focusFirstInvalidLocaleField()) return;

		if (this.focusFirstInvalidFieldTitle()) return;

		this.focusFirstInvalidOptionLocale();
	}

	public get selectedStepLocaleId(): string {
		return this.instructions?.selectedStepLocaleId ?? 'es';
	}

	public get showsInstructionsSectionDivider(): boolean {
		const preset = this.preset;
		return !(
			preset?.usesOptionsEditor ||
			preset?.usesRichTextOptionsEditor ||
			preset?.usesYesNoPreview ||
			preset?.usesLegalFieldsConfig
		);
	}

	private focusFirstInvalidFieldTitle(): boolean {
		if (!presetShowsFieldTitleEditor(this.preset)) {
			return false;
		}

		const allowedLocales = this.languageService.getAllowedLanguages();
		const invalidRef = firstInvalidFormStepFieldTitleRef(this.fieldsArray, allowedLocales);

		if (!invalidRef) {
			return false;
		}

		this.instructions?.selectLocaleTabPublic(invalidRef.localeId);
		focusFormStepFieldTitle();
		return true;
	}

	private focusFirstInvalidOptionLocale(): void {
		if (
			!this.preset?.usesOptionsEditor &&
			!this.preset?.usesRichTextOptionsEditor &&
			!this.preset?.usesLegalFieldsConfig
		) {
			return;
		}

		const optionsArray = this.primaryField?.get('options') as FormArray | null;
		if (!optionsArray) return;

		const allowedLocales = this.languageService.getAllowedLanguages();
		const richText = Boolean(this.preset.usesRichTextOptionsEditor || this.preset.usesLegalFieldsConfig);
		const invalidRef = firstInvalidFormStepOptionRef(this.fieldsArray, allowedLocales, richText);

		if (!invalidRef) return;

		this.instructions?.selectLocaleTabPublic(invalidRef.localeId);

		setTimeout(() => {
			focusFormStepOptionFieldIfInvalid(
				this.reactiveFormService,
				optionsArray,
				invalidRef.optionIndex,
				invalidRef.localeId,
				richText,
			);
		}, 320);
	}
}
